import {
  normalizeReportMatrix,
  observationsWithKeyIssues,
} from "./keyIssueMatrix";
const COMPLETED_REPORT_STATUSES = new Set([
  "completed",
  "generated",
  "reviewed",
  "archived",
]);

const WORK_SECTION_CATEGORIES: Record<string, string[]> = {
  "completed-sample-table": ["completed-works"],
  "key-activities-performed": ["key-activities-performed"],
  "key-activities-planned": ["key-activities-planned"],
  "third-party-activities": ["third-party-activities"],
  "construction-progress": ["construction-progress"],
  "lifting-activities": ["lifting-activities"],
  "hse-management": ["hse-incident", "hse-document-review"],
};

const OBSERVATION_SECTION_TYPES: Record<string, string[]> = {
  "overall-site-observations": ["general"],
  "key-issue-matrix": ["general"],
  "construction-aspects-operational": ["construction"],
};

const clone = <T>(value: T): T => {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
};

const normalizeKey = (value: any) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const hasValue = (value: any) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return Object.values(value).some((item) => hasValue(item));
  }
  return value !== undefined && value !== null && value !== "";
};

const compactObject = (value: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) => item !== undefined && item !== null,
    ),
  );

const getReportSiteId = (report: any) => {
  const site = report?.siteId || report?.site || report?.siteDataId;
  return typeof site === "object" ? site?._id || site?.id : site;
};

export const getCompletedReportsForSite = (
  allReportsData: Record<string, any> = {},
  currentSiteId?: string,
  currentReportId?: string,
) =>
  Object.values(allReportsData)
    .filter((reportData: any) => {
      const report = reportData?.report || {};
      const reportId = report?._id || report?.id;
      const isSameSite =
        String(getReportSiteId(report)) === String(currentSiteId);
      const isCompleted =
        COMPLETED_REPORT_STATUSES.has(report?.status) ||
        Boolean(report?.completedAt || report?.generatedPdf?.generated);

      return (
        reportId && reportId !== currentReportId && isSameSite && isCompleted
      );
    })
    .sort((a: any, b: any) => {
      const aDate = new Date(
        a?.report?.updatedAt || a?.report?.createdAt || 0,
      ).getTime();
      const bDate = new Date(
        b?.report?.updatedAt || b?.report?.createdAt || 0,
      ).getTime();
      return bDate - aDate;
    });

const pickRawSiteDataSection = (siteOverview: any) => ({
  environmentalData: siteOverview?.environmentalData,
  accessibilityInfo: siteOverview?.accessibilityInfo,
  facilities: siteOverview?.facilities,
  safetyMeasures: siteOverview?.safetyMeasures,
  description: siteOverview?.description,
  siteImages: siteOverview?.siteImages,
  additionalObservations: siteOverview?.additionalObservations,
});

export const getSiteDataImportPayload = (
  sectionName: string,
  sourceReportData: any,
) => {
  const siteOverview = sourceReportData?.siteOverview || {};

  switch (sectionName) {
    case "site-overview":
      return hasValue(siteOverview?.projectData)
        ? { projectData: clone(siteOverview.projectData) }
        : null;
    case "site-data":
      return hasValue(siteOverview?.siteData)
        ? { siteData: clone(siteOverview.siteData) }
        : compactObject(clone(pickRawSiteDataSection(siteOverview)));
    case "site-data-cdm":
      return hasValue(siteOverview?.siteData)
        ? { siteData: clone(siteOverview.siteData) }
        : null;
    case "lines-pipes-drain":
      return hasValue(siteOverview?.infrastructureData)
        ? { infrastructureData: clone(siteOverview.infrastructureData) }
        : null;
    case "grid-connection-information":
      return hasValue(siteOverview?.gridConnectionData)
        ? { gridConnectionData: clone(siteOverview.gridConnectionData) }
        : null;
    case "planning-aspects":
      return hasValue(siteOverview?.planningAspectsData)
        ? { planningAspectsData: clone(siteOverview.planningAspectsData) }
        : null;
    case "construction-aspects":
      return hasValue(siteOverview?.constructionAspectsData)
        ? {
            constructionAspectsData: clone(
              siteOverview.constructionAspectsData,
            ),
          }
        : null;
    case "operational-aspects":
      return hasValue(siteOverview?.operationalAspectsData)
        ? { operationalAspectsData: clone(siteOverview.operationalAspectsData) }
        : null;
    case "land-ownership":
      return hasValue(siteOverview?.landOwnershipData)
        ? { landOwnershipData: clone(siteOverview.landOwnershipData) }
        : null;
    case "shading-objects":
      return hasValue(siteOverview?.shadingObjectsData)
        ? { shadingObjectsData: clone(siteOverview.shadingObjectsData) }
        : null;
    case "construction-aspects-operational":
      return hasValue(siteOverview?.operationalData?.constructionAspects)
        ? {
            operationalData: {
              ...(siteOverview.operationalData || {}),
              constructionAspects: clone(
                siteOverview.operationalData.constructionAspects,
              ),
            },
          }
        : null;
    case "completed-sample-table":
      return hasValue(siteOverview?.punchList?.completedWorksDescription)
        ? {
            punchList: {
              completedWorksDescription: clone(
                siteOverview.punchList.completedWorksDescription,
              ),
            },
          }
        : null;
    case "key-activities-performed":
      return hasValue(
        siteOverview?.operationalAspectsData?.keyActivitiesPerformed,
      )
        ? {
            operationalAspectsData: {
              keyActivitiesPerformed: clone(
                siteOverview.operationalAspectsData.keyActivitiesPerformed,
              ),
            },
          }
        : null;
    case "key-activities-planned":
      return hasValue(siteOverview?.punchList?.keyActivitiesPlanned)
        ? {
            punchList: {
              keyActivitiesPlanned: clone(
                siteOverview.punchList.keyActivitiesPlanned,
              ),
            },
          }
        : null;
    case "third-party-activities":
      return hasValue(siteOverview?.punchList?.thirdPartyActivities)
        ? {
            punchList: {
              thirdPartyActivities: clone(
                siteOverview.punchList.thirdPartyActivities,
              ),
            },
          }
        : null;
    case "construction-progress":
      return hasValue(siteOverview?.punchList?.constructionProgress)
        ? {
            punchList: {
              constructionProgress: clone(
                siteOverview.punchList.constructionProgress,
              ),
            },
          }
        : null;
    case "lifting-activities":
      return hasValue(siteOverview?.punchList?.liftingActivities)
        ? {
            punchList: {
              liftingActivities: clone(
                siteOverview.punchList.liftingActivities,
              ),
            },
          }
        : null;
    case "hse-management":
      return hasValue(siteOverview?.punchList?.hseSummary)
        ? {
            punchList: { hseSummary: clone(siteOverview.punchList.hseSummary) },
          }
        : null;
    case "overall-site-observations":
      return hasValue(siteOverview?.overallSiteObservations)
        ? {
            overallSiteObservations: clone(
              siteOverview?.overallSiteObservations,
            ),
          }
        : null;
    default:
      return null;
  }
};

export const getLocalFormDataForSectionImport = (
  sectionName: string,
  sourceReportData: any,
) => {
  const siteOverview = sourceReportData?.siteOverview || {};

  switch (sectionName) {
    case "site-data":
      if (hasValue(siteOverview?.siteData)) return clone(siteOverview.siteData);
      return {
        topography:
          siteOverview?.environmentalData?.siteTopography?.topography || "",
        topographyNotes:
          siteOverview?.environmentalData?.siteTopography?.notes || "",
        accessToSite: siteOverview?.accessibilityInfo?.accessToSite || "",
        visibilitySplays:
          siteOverview?.accessibilityInfo?.visibilitySplays || "",
        narrowBridge: siteOverview?.accessibilityInfo?.narrowBridge || false,
        lowHeightBridge:
          siteOverview?.accessibilityInfo?.lowHeightBridge || false,
        accessRoadWidth: siteOverview?.accessibilityInfo?.accessRoadWidth || "",
        description: siteOverview?.description || [],
        groundRooftopCondition: siteOverview?.groundRooftopCondition || [],
        additionalObservations: siteOverview?.additionalObservations || "",
      };
    case "site-data-cdm":
      return clone(siteOverview?.siteData || {});
    case "completed-sample-table":
      return {
        completedWorksDescription:
          siteOverview?.punchList?.completedWorksDescription || "",
      };
    case "key-activities-performed":
      return clone(
        siteOverview?.operationalAspectsData?.keyActivitiesPerformed || {},
      );
    case "key-activities-planned":
      return clone(siteOverview?.punchList?.keyActivitiesPlanned || {});
    case "third-party-activities":
      return clone(siteOverview?.punchList?.thirdPartyActivities || {});
    case "construction-progress":
      return clone(siteOverview?.punchList?.constructionProgress || {});
    case "lifting-activities":
      return clone(siteOverview?.punchList?.liftingActivities || {});
    case "hse-management":
      return clone(siteOverview?.punchList?.hseSummary || {});
    case "overall-site-observations":
      return { summary: siteOverview?.overallSiteObservations || "" };
    case "construction-aspects-operational":
      return clone(siteOverview?.operationalData?.constructionAspects || {});
    default: {
      const payload = getSiteDataImportPayload(sectionName, sourceReportData);

      if (!payload) return null;
      const firstValue = Object.values(payload)[0];
      return clone(firstValue);
    }
  }
};

const SITE_OVERVIEW_SUBSECTION_FIELDS: Record<string, string[]> = {
  "basic-info": [
    "name",
    "country",
    "postcode",
    "town",
    "projectInformationNotes",
  ],
  "contact-info": [
    "projectManagerContact",
    "landlordContactDetails",
    "phone",
    "mobile",
    "fax",
    "email",
  ],
  "system-specification": [
    "moduleType",
    "inverterType",
    "mountingRack",
    "sizeOfArea",
    "plantCapacity",
    "exportCapacity",
    "dcCapacity",
    "acCapacity",
    "systemSpecificationNotes",
  ],
  "site-overview-image": ["projectImages"],
  "site-overview-notes": ["additionalNotes"],
};

const FORM_SUBSECTION_FIELDS: Record<string, Record<string, string[]>> = {
  "site-overview": SITE_OVERVIEW_SUBSECTION_FIELDS,
  "site-data": {
    topography: ["topography", "topographyNotes"],
    access: [
      "accessToSite",
      "visibilitySplays",
      "narrowBridge",
      "lowHeightBridge",
      "accessRoadWidth",
    ],
    description: ["description"],
    groundRooftopCondition: ["groundRooftopCondition"],
    observations: ["additionalObservations"],
    images: ["siteImages"],
  },
  "site-data-cdm": {
    topography: ["topography", "topographyNotes"],
    facilities: [
      "carParkAvailable",
      "carParkAvailableNotes",
      "storageAvailable",
      "storageAvailableNotes",
      "safetyAvailable",
      "safetyAvailableNotes",
      "welfareFacilitiesAvailable",
      "welfareFacilitiesAvailableNotes",
    ],
    access: ["siteAccess", "siteAccessNotes"],
    "ground-conditions": ["groundConditions", "groundConditionsNotes"],
    "mvs-delivery": ["mvsDelivery", "mvsDeliveryNotes"],
    comments: ["comments"],
    images: ["siteImages"],
  },
  "lines-pipes-drain": {
    // types: ["infrastructureTypes"],
    // details: ["additionalDetails", "locationDescription", "depthInformation"],
    // "safety-access": ["safetyConsiderations", "accessPoints"],
    // notes: ["generalNotes", "surveyNotes", "maintenanceNotes"],
    images: ["infrastructureImages"],

    "lines-pipes-drain-info": [
      "infrastructureTypes",
      "additionalDetails",
      "locationDescription",
      "depthInformation",
      "safetyConsiderations",
      "accessPoints",
      "generalNotes",
      "surveyNotes",
      "maintenanceNotes",
    ],
  },
  "grid-connection-information": {
    // interconnection: ["distanceToInterconnection", "cableRouteConstraints", "voltageLevel"],
    // utility: ["utilityCompanyConditions"],
    // equipment: ["substationProvider", "transformerDetails", "switchgearDetails", "meteringSystem"],
    // "grounding-risk": ["groundingLightningProtection", "substationFloodRisk"],
    images: ["gridConnectionImages"],

    "grid-connection-info": [
      "distanceToInterconnection",
      "cableRouteConstraints",
      "voltageLevel",
      "utilityCompanyConditions",
      "substationProvider",
      "transformerDetails",
      "switchgearDetails",
      "meteringSystem",
      "groundingLightningProtection",
      "substationLocation",
      "floodRiskPresent",
      "gridConnectionRemarks",
    ],
  },
  "planning-aspects": {
    // "authority-visibility": [
    //   "visibilityFromDwellings",
    //   "visibilityFromCommercial",
    //   "authorityConsultationCompleted",
    //   "authorityFeedback",
    // ],
    // "site-designations": ["siteDesignations", "otherDesignation"],
    // "utility-substation": ["utilityCompanyConditions", "utilityCompanyRequirements", "substationDeliveryBy"],
    images: ["planningAspectsImages"],
    // "screening-landscape": ["screeningConditions", "landscapeManagementPlan"],
    "planning-aspects-info": [
      "visibilityFromDwellings",
      "visibilityFromCommercial",
      "authorityConsultationCompleted",
      "authorityFeedback",
      "siteDesignations",
      "otherDesignation",
      "utilityCompanyConditions",
      "utilityCompanyRequirements",
      "substationDeliveryBy",
      "screeningConditions",
      "landscapeManagementPlan",
      "planningAspectsRemarks",
    ],
  },
  "construction-aspects": {
    // access: ["constructionAccessDescription", "accessibleBy38tLorries"],
    // "ground-traffic": ["groundConditionRisks", "trafficImpact"],
    // logistics: ["offloadingAreaAvailable", "waterPowerAvailability"],
    // remarks: ["otherRemarks"],
    images: ["constructionAspectsImages"],
    "construction-aspects-info": [
      "constructionAccessDescription",
      "accessibleBy38tLorries",
      "groundConditionRisks",
      "trafficImpact",
      "offloadingAreaAvailable",
      "waterPowerAvailability",
      "otherRemarks",
    ],
  },
  "land-ownership": {
    // ownership: ["ownershipType"],
    // crossings: [
    //   "gridRouteCrossingOtherLand",
    //   "constructionAccessCrossingOtherLand",
    //   "accessRetainedForOM",
    // ],
    // risks: ["unregisteredLand", "disputedLand", "other"],
    images: ["landOwnershipImages"],
    "land-ownership-info": [
      "ownershipType",
      "gridRouteCrossingOtherLand",
      "constructionAccessCrossingOtherLand",
      "accessRetainedForOM",
      "unregisteredLand",
      "disputedLand",
      "other",
    ],
  },
  "shading-objects": {
    // presence: ["shadingObjectsPresent"],
    // dimensions: ["heightWidth"],
    // "removal-growth": ["removableBeforeConstruction", "futureGrowthExpected"],
    // remarks: ["other"],
    images: ["shadingObjectsImages"],
    "shading-objects-info": [
      "shadingObjectsPresent",
      "heightWidth",
      "removableBeforeConstruction",
      "futureGrowthExpected",
      "other",
    ],
  },
  "operational-aspects": {
    civil: [
      "civilSiteConditions",
      "civilSiteAccessRoadCondition",
      "civilLVCablesManholesCondition",
      "civilHVCablesManholesCondition",
      "civilFoundationCondition",
      "civilTransformerCondition",
    ],
    mechanical: [
      "mechanicalFrameworkAppropriateRammingHead",
      "mechanicalFrameworkAlignmentTwisted",
      "mechanicalFrameworkRammingDepth",
      "mechanicalFrameworkUndergroundSurprises",
      "mechanicalFrameworkRegalvanizationMethod",
      "mechanicalFrameworkDrillingConcrete",
      "mechanicalFrameworkAverageRemainingTime",
      "mechanicalFrameworkSignsOfCorrosion",
      "mechanicalPurlinSignsOfCorrosion",
      "mechanicalPurlinVisibleDeformation",
      "mechanicalModuleSynchronizedAlignment",
      "mechanicalModuleClampInstallationCheck",
    ],
    electrical: [
      "electricalStringAboveLooseCableConnector",
      "electricalStringAboveProperSupport",
      "electricalStringAboveNoSharpEdge",
      "electricalStringAboveCableNumbering",
      "electricalStringBelowRoutedThroughConduit",
      "electricalStringBelowConduitSealedWithFoam",
      "electricalDCMainCablesWithoutEndCap",
      "electricalDCMainCableSpecification",
      "electricalDCMainInstallationMethodology",
      "electricalDCMainBimetallicCompatibility",
      "electricalDCMainCableJointsCrimpsDies",
    ],
    other: ["otherWaterIngressManholes", "otherCableGlandsCondition"],
    images: ["operationalAspectsImages"],
  },
  "construction-aspects-operational": {
    civil: [
      "siteObservations",
      "civil_conditionOfSiteAccessRoad",
      "civil_constructionCompoundCondition",
      "civil_trafficProblemsForResidents",
      "civil_separateOffloadingArea",
      "civil_onsiteTrafficManagement",
      "civil_vehiclesSuitableForSite",
      "civil_lvTrenchCondition",
      "civil_hvTrenchCondition",
      "civil_groundConditionProblems",
      "civil_civilNotes",
    ],
    mechanical: [
      "moduleInstallationCompliant",
      "moduleInstallationNotes",
      "moduleClampingCompliant",
      "moduleClampingNotes",
      "structureAlignmentCompliant",
      "structureMisalignmentDetails",
      "mechanicalStressConcerns",
      "mechanical_pilingQuality",
      "mechanical_mountingAngleAdjustment",
      "mechanical_rammingHeadProfile",
      "mechanical_rammingDepth",
      "mechanical_undergroundSurprises",
      "mechanical_alignmentTwisted",
      "mechanical_regalvanizationMethod",
      "mechanical_drillingConcrete",
      "mechanical_averageRemainingTime",
      "mechanical_trafficProblemsForResidents",
      "mechanical_separateOffloadingArea",
      "mechanical_vehiclesSuitableForSite",
      "mechanical_onsiteTrafficManagement",
      "mechanical_lvTrenchCondition",
      "mechanical_hvTrenchCondition",
      "mechanical_groundConditionProblems",
      "mechanical_mechanicalNotes",
    ],
    electrical: [
      "electrical_stringCableType",
      "electrical_looseCableEnds",
      "electrical_mc4ConnectorSupport",
      "electrical_cableSupport",
      "electrical_sharpEdgeExposure",
      "electrical_cableStringNumbering",
      "electrical_conduitManagement",
      "electrical_conduitFilledWithFoam",
      "electrical_cablesWithoutEndCap",
      "electrical_cableSpecification",
      "electrical_cableInstallationMethodology",
      "electrical_biMetallicCompatibility",
      "electrical_cableConnectorsCrimpsDies",
      "electrical_electricalNotes",
      "electrical_stringAboveLooseCableConnector",
      "electrical_stringAboveProperSupport",
      "electrical_stringAboveNoSharpEdge",
      "electrical_stringAboveCableNumbering",
      "electrical_stringBelowRoutedThroughConduit",
      "electrical_stringBelowConduitSealedWithFoam",
      "electrical_dcMainCablesWithoutEndCap",
      "electrical_dcMainCableSpecification",
      "electrical_dcMainInstallationMethodology",
      "electrical_dcMainBimetallicCompatibility",
      "electrical_dcMainCableJointsCrimpsDies",
    ],
    "module-cable": [
      "moduleInstallationCompliant",
      "moduleClampingCompliant",
      "moduleInstallationNotes",
      "moduleClampingNotes",
      "structureAlignmentCompliant",
      "structureMisalignmentDetails",
      "mechanicalStressConcerns",
      "cableBendingRadiusCompliant",
      "cableTensionCompliant",
      "cableManagementNotes",
      "cableLayingCompliant",
      "cableSupportCompliant",
      "mc4ConnectorSupportCompliant",
      "cableSupportNotes",
      "cableProtectionCompliant",
      "sharpEdgeProtectionCompliant",
      "protectionIssuesDetails",
      "cableNumberingCompliant",
      "numberingSystemDescription",
      "numberingIssuesDetails",
      "mechanicalProtectionCompliant",
      "cableNumberingSystemDescription",
    ],
    observations: ["constructionObservations", "observationsSummary"],
    actions: ["correctiveActions", "priorityLevel"],
    images: ["constructionImages"],
  },
  "completed-sample-table": {
    description: ["completedWorksDescription"],
  },
  "key-activities-performed": {
    summary: ["overallSummary", "overallComment", "comments"],
  },
  "key-activities-planned": {
    summary: ["overallSummary", "overallComment", "comments"],
  },
  "third-party-activities": {
    summary: ["overallSummary", "comments"],
  },
  "construction-progress": {
    summary: ["overallSummary", "comments"],
  },
  "lifting-activities": {
    summary: ["summary", "description", "comments"],
  },
  "hse-management": {
    induction: ["inductionProcess", "visitorSignInBookChecked"],
    security: ["securityMeasures", "safetyPlan"],
    training: ["trainingCompleted", "inspectionDate", "inspectionNotes"],
    recommendations: ["recommendations"],
  },
  "overall-site-observations": {
    summary: ["summary"],
  },
  "key-issue-matrix": {
    assessment: ["overallRiskLevel", "notes"],
  },
};

const IMPORT_SUBSECTION_OPTIONS: Record<
  string,
  { value: string; label: string }[]
> = {
  "site-overview": [
    { value: "basic-info", label: "Basic Info" },
    { value: "contact-info", label: "Contact Info" },
    { value: "system-specification", label: "System Specification" },
    { value: "site-overview-image", label: "Site Overview Image" },
    { value: "site-overview-notes", label: "Site Overview Notes" },
  ],
  "site-data": [
    { value: "topography", label: "Site Topography" },
    { value: "access", label: "Access to Site" },
    { value: "description", label: "Site Description" },
    { value: "groundRooftopCondition", label: "Ground / Rooftop Condition" },
    { value: "observations", label: "Additional Observations" },
    // { value: "images", label: "Site Images" },
  ],
  "site-data-cdm": [
    { value: "topography", label: "Site Topography" },
    { value: "facilities", label: "Facilities & Services" },
    { value: "access", label: "Access to Site" },
    { value: "ground-conditions", label: "Ground Conditions" },
    { value: "mvs-delivery", label: "Delivery of MVS" },
    { value: "comments", label: "Comments" },
    // { value: "images", label: "Site Photos" },
  ],
  "lines-pipes-drain": [
    // { value: "types", label: "Infrastructure Types" },
    // { value: "details", label: "Location & Depth Details" },
    // { value: "safety-access", label: "Safety & Access" },
    // { value: "notes", label: "Notes" },
    // { value: "images", label: "Images" },
    { value: "lines-pipes-drain-info", label: "Lines, Pipes & Drain" },
  ],
  "grid-connection-information": [
    // { value: "interconnection", label: "Interconnection" },
    // { value: "utility", label: "Utility Conditions" },
    // { value: "equipment", label: "Equipment Details" },
    // { value: "grounding-risk", label: "Grounding & Flood Risk" },
    // { value: "images", label: "Images" },

    { value: "grid-connection-info", label: "Grid Connection Information" },
  ],
  "planning-aspects": [
    // { value: "authority-visibility", label: "Authority & Visibility" },
    // { value: "site-designations", label: "Site Designations" },
    // { value: "utility-substation", label: "Utility & Substation" },
    // { value: "screening-landscape", label: "Screening & Landscape" },
    // { value: "images", label: "Images" },

    { value: "planning-aspects-info", label: "Planning Aspects Information" },
  ],
  "construction-aspects": [
    // { value: "access", label: "Construction Access" },
    // { value: "ground-traffic", label: "Ground & Traffic Risks" },
    // { value: "logistics", label: "Logistics" },
    // { value: "remarks", label: "Remarks" },
    // { value: "images", label: "Images" },
    {
      value: "construction-aspects-info",
      label: "Construction Aspects Information",
    },
  ],
  "land-ownership": [
    // { value: "ownership", label: "Ownership Type" },
    // { value: "crossings", label: "Crossings & Access Rights" },
    // { value: "risks", label: "Land Risks & Remarks" },
    // { value: "images", label: "Images" },
    { value: "land-ownership-info", label: "Land Ownership Information" },
  ],
  "shading-objects": [
    // { value: "presence", label: "Shading Presence" },
    // { value: "dimensions", label: "Dimensions" },
    // { value: "removal-growth", label: "Removal & Future Growth" },
    // { value: "remarks", label: "Remarks" },
    // { value: "images", label: "Images" },
    { value: "shading-objects-info", label: "Shading Objects Information" },
  ],
  "operational-aspects": [
    { value: "civil", label: "Civil" },
    { value: "mechanical", label: "Mechanical" },
    { value: "electrical", label: "Electrical" },
    { value: "other", label: "Other" },
    // { value: "images", label: "Images" },
  ],
  "construction-aspects-operational": [
    { value: "civil", label: "Civil Aspects" },
    { value: "mechanical", label: "Mechanical Aspects" },
    { value: "electrical", label: "Electrical Aspects" },
    { value: "module-cable", label: "Module & Cable Management" },
    { value: "observations", label: "Construction Observations" },
    // { value: "actions", label: "Corrective Actions & Priority" },
    { value: "images", label: "Construction Images" },
  ],
  "completed-sample-table": [
    { value: "description", label: "Description" },
    { value: "mechanical", label: "Mechanical Works" },
    { value: "electrical", label: "Electrical Works" },
    { value: "civil", label: "Civil Works" },
  ],
  "key-activities-performed": [
    { value: "summary", label: "Summary & Comments" },
    { value: "mechanical", label: "Mechanical Activities" },
    { value: "electrical", label: "Electrical Activities" },
    { value: "civil", label: "Civil Activities" },
    { value: "other", label: "Other Activities" },
  ],
  "key-activities-planned": [
    { value: "summary", label: "Summary & Comments" },
    { value: "civil", label: "Civil Activities" },
    { value: "electrical", label: "Electrical Activities" },
    { value: "mechanical", label: "Mechanical Activities" },
    // { value: "dno", label: "DNO Activities" },
    // { value: "scada", label: "SCADA Activities" },
  ],
  "third-party-activities": [
    { value: "summary", label: "Summary & Comments" },
    { value: "activities", label: "Third-Party Activities" },
  ],
  "construction-progress": [
    { value: "summary", label: "Summary & Comments" },
    { value: "civil", label: "Civil Phases" },
    { value: "mechanical", label: "Mechanical Phases" },
    { value: "electrical", label: "Electrical Phases" },
    // { value: "dno", label: "DNO Phases" },
    // { value: "foundation", label: "Foundation Phases" },
    // { value: "commissioning", label: "Commissioning Phases" },
  ],
  "lifting-activities": [
    { value: "summary", label: "Summary & Comments" },
    { value: "activities", label: "Lifting Activities" },
  ],
  "hse-management": [
    { value: "induction", label: "Induction & Sign-In" },
    { value: "security", label: "Security & Safety Plan" },
    { value: "training", label: "Training & Inspection" },
    { value: "recommendations", label: "Recommendations" },
    { value: "incidents", label: "HSE Incidents" },
    { value: "documents", label: "HSE Documents" },
  ],
  "overall-site-observations": [
    { value: "summary", label: "Observations Summary" },
    { value: "observations", label: "Observation Items" },
  ],
  "key-issue-matrix": [{ value: "risks", label: "Risk Matrix Items" }],
};

export const getImportSubsectionOptions = (sectionName: string) => {
  return IMPORT_SUBSECTION_OPTIONS[sectionName] || [];
};

export const filterLocalFormDataBySubsections = (
  sectionName: string,
  formData: any,
  selectedSubsections: string[] = [],
) => {
  if (!selectedSubsections.length) return formData;

  const subsectionFields = FORM_SUBSECTION_FIELDS[sectionName];
  if (!subsectionFields) return formData;
  const selectedFields = new Set(
    selectedSubsections.flatMap(
      (subsection) => subsectionFields[subsection] || [],
    ),
  );

  return Object.fromEntries(
    Object.entries(formData || {}).filter(([field]) =>
      selectedFields.has(field),
    ),
  );
};

const itemMatchesAnySubsection = (
  item: any,
  selectedSubsections: string[] = [],
) => {
  if (!selectedSubsections.length) return true;

  const type =
    item?.workType ||
    item?.category ||
    item?.type ||
    item?.customCategory ||
    item?.workCategory ||
    "";

  if (
    selectedSubsections.includes("activities") ||
    selectedSubsections.includes("observations") ||
    selectedSubsections.includes("risks")
  ) {
    return true;
  }

  if (
    selectedSubsections.includes("incidents") &&
    item?.workCategory === "hse-incident"
  )
    return true;
  if (
    selectedSubsections.includes("documents") &&
    item?.workCategory === "hse-document-review"
  )
    return true;

  return selectedSubsections.some((subsection) =>
    String(type).toLowerCase().includes(subsection),
  );
};

const getFileId = (file: any) => file?._id || file?.id;

const createImportedFileRecord = (
  file: any,
  siteId?: string,
  reportId?: string,
  relatedTo?: any,
  importScope: "section" | "collection-item" = "section",
) => {
  const sourceFileId = getFileId(file);
  const pendingFileId = `pending-${sourceFileId || Date.now()}-${normalizeKey(
    relatedTo?.entityType || file?.relatedTo?.entityType,
  )}-${normalizeKey(relatedTo?.entityId || file?.relatedTo?.entityId)}-${normalizeKey(
    JSON.stringify(relatedTo?.filterData || file?.relatedTo?.filterData || {}),
  )}`;

  return {
    ...clone(file),
    _id: pendingFileId,
    id: pendingFileId,
    sourceFileId,
    siteId: siteId || file?.siteId,
    reportId: reportId || file?.reportId,
    relatedTo: {
      ...(file?.relatedTo || {}),
      ...(relatedTo || {}),
      filterData:
        relatedTo?.filterData ||
        file?.relatedTo?.filterData ||
        file?.metadata?.filterData ||
        {},
    },
    metadata: {
      ...(file?.metadata || {}),
      importedFromFileId: sourceFileId,
      importedFromReportId: file?.reportId,
      importScope,
      pendingImport: true,
    },
    isImported: true,
  };
};

const getFilesForCollectionItem = (
  sourceReportData: any,
  entityType: string,
  entityId: any,
) =>
  (sourceReportData?.files || []).filter((file: any) => {
    if (String(file?.relatedTo?.entityId) !== String(entityId)) return false;
    if (entityType === "observation") {
      return file?.relatedTo?.entityType === "observation";
    }
    return file?.relatedTo?.entityType !== "observation";
  });

const sanitizeCollectionItem = (
  item: any,
  sourceReportData: any,
  siteId?: string,
  reportId?: string,
  entityType?: "work" | "observation",
) => {
  const sourceId = item?._id || item?.id;
  const importedId = `imported-${sourceId || Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  const pendingImportedFiles = entityType
    ? getFilesForCollectionItem(sourceReportData, entityType, sourceId).map(
        (file: any) =>
          createImportedFileRecord(
            file,
            siteId,
            reportId,
            {
              ...(file?.relatedTo || {}),
              entityId: importedId,
            },
            "collection-item",
          ),
      )
    : [];
  const next = clone(item);
  if (entityType === "observation" && Array.isArray(next?.keyIssues)) {
    next.keyIssues = next.keyIssues.map((risk: any) => {
      const sourceRiskId = risk?._id || risk?.id;
      const localRiskId = `imported-risk-${sourceRiskId || Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const riskFiles = (sourceReportData?.files || [])
        .filter(
          (file: any) =>
            String(file?.relatedTo?.entityType) === "key-issue-matrix" &&
            String(file?.relatedTo?.entityId) === String(sourceRiskId),
        )
        .map((file: any) =>
          createImportedFileRecord(
            file,
            siteId,
            reportId,
            {
              ...(file?.relatedTo || {}),
              entityType: "key-issue-matrix",
              entityId: localRiskId,
            },
            "collection-item",
          ),
        );
      pendingImportedFiles.push(...riskFiles);
      const { _id, ...riskData } = risk;
      return { ...riskData, id: localRiskId, _sourceRiskId: sourceRiskId };
    });
  }
  [
    "_id",
    "id",
    "__v",
    "createdAt",
    "updatedAt",
    "completedDate",
    "files",
    "attachments",
  ].forEach((key) => delete next?.[key]);

  return {
    ...next,
    id: importedId,
    importedFrom: sourceId,
    _importedLocalId: importedId,
    _sourceId: sourceId,
    _pendingImportedFiles: pendingImportedFiles,
    ...(siteId ? { siteId } : {}),
    ...(reportId ? { reportId } : {}),
  };
};

const SECTION_FILE_ENTITY_MATCHERS: Record<string, string[]> = {
  "site-overview": ["project"],
  "site-data": ["site-data"],
  "site-data-cdm": ["site-data-cdm"],
  "lines-pipes-drain": ["lines-pipes-drain"],
  "grid-connection-information": ["grid-connection-information"],
  "planning-aspects": ["planning-aspects"],
  "construction-aspects": ["construction-aspects"],
  "land-ownership": ["land-ownership"],
  "shading-objects": ["shading-objects"],
  "operational-aspects": ["operational-aspects"],
  "construction-aspects-operational": ["construction-aspects"],
  "completed-sample-table": ["completed-sample-table"],
  "key-activities-performed": ["key-activities", "key-activities-performed"],
  "key-activities-planned": ["key-activities-planned"],
  "third-party-activities": ["third-party-activities"],
  "construction-progress": ["construction-progress"],
  "lifting-activities": ["lifting-activities", "mvs-css-lifting"],
  "hse-management": ["hse-management"],
  "overall-site-observations": ["observation"],
  "key-issue-matrix": ["key-issue-matrix"],
};

const SUBSECTION_FILE_ALIASES: Record<string, string[]> = {
  "site-overview-image": ["projectimages", "projectimagesmain"],
  topography: ["topography"],
  access: ["access", "siteaccess", "accesstosite"],
  facilities: ["carpark", "storage", "safety", "welfare"],
  "ground-conditions": ["groundconditions"],
  "mvs-delivery": ["mvs", "mvsstation", "delivery"],
  images: ["image", "images", "photo", "photos", "main"],
  civil: ["civil"],
  mechanical: ["mechanical"],
  electrical: ["electrical"],
  "module-cable": ["module", "cable"],
  observations: ["generalsite"],
  risks: ["risk"],
  incidents: ["incident"],
  documents: ["document"],
  "grid-connection-info": ["gridconnection"],
  "planning-aspects-info": ["planningaspects"],
  "construction-aspects-info": ["constructionaspects"],
  "land-ownership-info": ["landownership"],
  "shading-objects-info": ["shadingobjects"],
  "operational-aspects-info": ["operationalaspects"],
};

const SITE_SCOPED_MAIN_FILE_SECTIONS = new Set([
  "site-data-cdm",
  "planning-aspects",
  "construction-aspects",
  "land-ownership",
  "shading-objects",
  "operational-aspects",
]);

const fileMatchesSection = (sectionName: string, file: any) => {
  const entityType = normalizeKey(file?.relatedTo?.entityType);
  const matchers = SECTION_FILE_ENTITY_MATCHERS[sectionName] || [sectionName];

  // console.log(entityType,matchers)
  return matchers.some((matcher) => entityType.includes(normalizeKey(matcher)));
};
const fileMatchesSubsections = (
  file: any,
  selectedSubsections: string[] = [],
) => {
  if (!selectedSubsections.length) return true;
  const filterText = Object.keys(file?.relatedTo?.filterData || {})
    .map(normalizeKey)
    .join(" ");
  return selectedSubsections.some((subsection) => {
    // console.log("subsection ", subsection, " filterText ", filterText);
    const aliases = SUBSECTION_FILE_ALIASES[subsection] || [subsection];
    return aliases.some((alias) => filterText.includes(normalizeKey(alias)));
  });
};

const getTargetSectionFileEntityId = (
  sectionName: string,
  file: any,
  siteId?: string,
  reportId?: string,
) => {
  const filterKeys = Object.keys(file?.relatedTo?.filterData || {}).map(
    normalizeKey,
  );
  const isMainSectionFile = filterKeys.some((key) => key.includes("main"));
  if (isMainSectionFile && SITE_SCOPED_MAIN_FILE_SECTIONS.has(sectionName)) {
    return siteId || reportId || file?.relatedTo?.entityId;
  }
  return reportId || file?.relatedTo?.entityId;
};

export const getImportedFilesForSectionImport = (
  sectionName: string,
  sourceReportData: any,
  siteId?: string,
  reportId?: string,
  selectedSubsections: string[] = [],
) => {
  return (sourceReportData?.files || [])
    .filter((file: any) => file?.relatedTo?.entityType !== "work")
    .filter((file: any) => file?.relatedTo?.entityType !== "observation")
    .filter((file: any) => fileMatchesSection(sectionName, file))
    .filter((file: any) => fileMatchesSubsections(file, selectedSubsections))
    .map((file: any) =>
      createImportedFileRecord(
        file,
        siteId,
        reportId,
        {
          ...(file?.relatedTo || {}),
          entityId: getTargetSectionFileEntityId(
            sectionName,
            file,
            siteId,
            reportId,
          ),
        },
        "section",
      ),
    );
};

export const getPendingImportedFilesFromItems = (items: any[] = []) =>
  items.flatMap((item: any) => item?._pendingImportedFiles || []);

export const getWorksForSectionImport = (
  sectionName: string,
  sourceReportData: any,
  siteId?: string,
  reportId?: string,
  selectedSubsections: string[] = [],
) => {
  const categories = WORK_SECTION_CATEGORIES[sectionName] || [];
  if (!categories.length) return [];

  return (sourceReportData?.works || [])
    .filter((work: any) => categories.includes(work?.workCategory))
    .filter((work: any) => itemMatchesAnySubsection(work, selectedSubsections))
    .map((work: any) =>
      sanitizeCollectionItem(work, sourceReportData, siteId, reportId, "work"),
    );
};

export const getObservationsForSectionImport = (
  sectionName: string,
  sourceReportData: any,
  siteId?: string,
  reportId?: string,
  selectedSubsections: string[] = [],
) => {
  const types = OBSERVATION_SECTION_TYPES[sectionName] || [];
  if (!types.length) return [];

  const normalized = normalizeReportMatrix(sourceReportData);
  let observations = (
    sectionName === "key-issue-matrix"
      ? observationsWithKeyIssues(
          normalized.observations,
          normalized.keyIssueMatrixItems,
        )
      : normalized.observations || []
  ).filter((observation: any) => types.includes(observation?.observationType));

  if (sectionName === "key-issue-matrix") {
    observations = observations.filter((observation: any) =>
      hasValue(observation?.keyIssues),
    );
  }

  observations = observations.filter((observation: any) =>
    itemMatchesAnySubsection(observation, selectedSubsections),
  );

  return observations.map((observation: any) =>
    sanitizeCollectionItem(
      observation,
      sourceReportData,
      siteId,
      reportId,
      "observation",
    ),
  );
};

export const sectionHasImportableData = (
  sectionName: string,
  sourceReportData: any,
) =>
  hasValue(getSiteDataImportPayload(sectionName, sourceReportData)) ||
  (sourceReportData?.files || []).some(
    (file: any) =>
      file?.relatedTo?.entityType !== "work" &&
      file?.relatedTo?.entityType !== "observation" &&
      fileMatchesSection(sectionName, file),
  ) ||
  (sourceReportData?.works || []).some((work: any) =>
    (WORK_SECTION_CATEGORIES[sectionName] || []).includes(work?.workCategory),
  ) ||
  (sourceReportData?.observations || []).some((observation: any) => {
    const types = OBSERVATION_SECTION_TYPES[sectionName] || [];
    if (!types.includes(observation?.observationType)) return false;
    return sectionName === "key-issue-matrix"
      ? hasValue(
          normalizeReportMatrix(sourceReportData).keyIssueMatrixItems.filter(
            (item: any) =>
              String(item.observationId) === String(observation._id),
          ),
        )
      : true;
  });

// ---------------------------------------------------------------------------
// Shared helpers for de-duplicating imports and persisting imported works /
// observations. These are intentionally centralized here (rather than
// re-implemented per component) so that every section that supports
// importing works/observations behaves consistently: re-importing the same
// source report never creates duplicate items, and imported-but-unsaved
// items are always included when a component's "Save" handler runs.
// ---------------------------------------------------------------------------

/**
 * Returns the source id (`importedFrom`) that identifies which item in the
 * source report a given work/observation/candidate was imported from.
 * Works for both already-persisted items (which carry `importedFrom` once
 * saved) and pending, not-yet-saved items produced by `sanitizeCollectionItem`
 * (which carry `_sourceId` locally until they are saved).
 */
const getImportSourceId = (item: any) => item?.importedFrom || item?._sourceId;

/**
 * Filters candidate works/observations (freshly built from a source report)
 * down to the ones that have not already been imported into `existingItems`
 * (the works/observations currently attached to this report, whether already
 * saved to the database or still pending save in this session).
 */
export const filterAlreadyImportedItems = (
  existingItems: any[] = [],
  candidateItems: any[] = [],
) => {
  const alreadyImportedSourceIds = new Set(
    (existingItems || [])
      .map((item: any) => getImportSourceId(item))
      .filter(Boolean)
      .map((id: any) => String(id)),
  );

  return (candidateItems || []).filter((item: any) => {
    const sourceId = getImportSourceId(item);
    return !sourceId || !alreadyImportedSourceIds.has(String(sourceId));
  });
};

/**
 * Finds a work/observation in `progressData` (the component's local
 * work/observation progress list) by either its saved `_id` or its local
 * `id`.
 */
export const findProgressItem = (progressData: any[] = [], itemId: string) =>
  (progressData || []).find(
    (item: any) => item?._id === itemId || item?.id === itemId,
  );

/**
 * Returns true when `itemId` refers to an item that was imported from
 * another report but has not been persisted to the database yet (it has a
 * source id but no real `_id`).
 */
export const isPendingImportedItem = (
  progressData: any[] = [],
  itemId: string,
) => {
  const existing = findProgressItem(progressData, itemId);
  return Boolean(existing && getImportSourceId(existing) && !existing?._id);
};

/**
 * Builds the list of `[id, editedFields]` entries a save handler should
 * persist: every entry the user actually edited, plus any imported items
 * sitting in `progressData` that have not been saved yet (so a save
 * triggered without editing a field — e.g. right after an import — still
 * persists the imported items).
 */
export const buildSaveEntriesWithPendingImports = (
  progressData: any[] = [],
  editedData: Record<string, any> = {},
) => {
  const pendingImportedEntries = (progressData || [])
    .filter((item: any) => getImportSourceId(item) && !item?._id)
    .map((item: any) => [item.id, item] as [string, any]);

  return [
    ...Object.entries(editedData || {}),
    ...pendingImportedEntries.filter(([id]) => !(editedData || {})[id]),
  ];
};

/**
 * Builds the payload to send to `createWork`/`createObservation` for a
 * pending imported item, merging the sanitized imported item with any
 * locally edited fields and caller-supplied fields (siteId/reportId/etc.),
 * and stripping the client-only bookkeeping fields used to track the import.
 */
export const buildImportedItemPayload = (
  existingItem: any,
  editedFields: Record<string, any> = {},
  extraFields: Record<string, any> = {},
) => {
  const { id, _importedLocalId, _sourceId, _pendingImportedFiles, ...rest } =
    existingItem || {};

  return {
    ...rest,
    ...editedFields,
    ...extraFields,
  };
};
